import { IssuesTypeList } from '@host/pages/Issues/Components/organisms/IssuesTypeList'
import { Box, Container, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { CompaniesDialogContainer } from '@tom-ui/billing'
import { workInstructionService } from '@tom-ui/operations'
import { InitializationWrapper, IssuesPageLocationState } from '@tom-ui/planning'
import { ResolveIssueDialog } from '@tom-ui/planning/pages/Issues/Components/templates/ResolveIssueDialog'
import { NoListResult, useMinimalsTheme } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import { YardIssueResolveDialog } from '@tom-ui/yard'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useLocation } from 'react-router'
import { IssuesList } from './Components/organisms/IssuesList'
import { IssuesPageHeader } from './Components/organisms/IssuesPageHeader'
import { PositionMissingDialog } from './Components/PositionMissingDialog'
import { useAggregateIssuesViewStore } from './hooks/useAggregateIssuesViewStore'
import { applyIssuesQueryFilters } from './utils/issue-query-filter.util'

export const Issue = observer(() => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()
  const { state, search } = useLocation() as {
    state: IssuesPageLocationState
    search: string
  }

  const { aggregateIssuesViewStore } = useAggregateIssuesViewStore()
  const {
    issueViewStore: planningIssueViewStore,
    issueResolutionViewStore: planningIssueResolutionViewStore,
  } = aggregateIssuesViewStore.planningStores
  const { issueViewStore: operationIssueViewStore } = aggregateIssuesViewStore.operationStores

  useEffect(() => {
    applyIssuesQueryFilters(search, aggregateIssuesViewStore)
  }, [aggregateIssuesViewStore, search])

  useEffect(() => {
    if (!state) return

    if (state.domain === 'Planning' && state.id) {
      const issue = planningIssueViewStore.dataFilteredByIssueStatus.find(
        (i: any) => i.id === state.id,
      )
      if (issue) planningIssueResolutionViewStore.openDialog(issue)
    } else if (state?.filterByRailCarTrackPositionId) {
      planningIssueViewStore.setFilterByRailCarTrackPositionId(
        state?.filterByRailCarTrackPositionId,
      )
      operationIssueViewStore.setFilterByRailCarTrackPositionId(
        state?.filterByRailCarTrackPositionId,
      )
    } else if (state?.visitId) {
      planningIssueViewStore.setFilterByVisitId(state?.visitId)
      operationIssueViewStore.setFilterByVisitId(state?.visitId)
    }
  }, [planningIssueViewStore, planningIssueResolutionViewStore, state, operationIssueViewStore])

  useEffect(() => {
    // Reset filter state on unmount so singleton operations/yard UI stores don't
    // leak selections (e.g. selectedIssueType) into the next visit and hide tiles.
    return () => {
      aggregateIssuesViewStore.clearFilters()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { loading, error } = useAsyncFetch(async () => {
    await aggregateIssuesViewStore.fetch()
  }, [aggregateIssuesViewStore])

  if (error) {
    return (
      <>
        <Typography variant='h4'>{t('errorLoadingIssues', 'Error loading issues')}</Typography>
      </>
    )
  }

  return (
    <InitializationWrapper isInitializing={loading}>
      <Box sx={{ height: '100%' }}>
        <IssuesPageHeader store={aggregateIssuesViewStore} />
        <Container sx={{ paddingY: theme.customSpacing.m }}>
          <Stack gap={theme.customSpacing.m}>
            {aggregateIssuesViewStore.dataFilteredByIssueStatusCount === 0 && (
              <NoListResult
                result={t('noIssuesFound', 'No issues found')}
                filtering={
                  aggregateIssuesViewStore.isFilterEnabled ||
                  !!aggregateIssuesViewStore.containerNumberFilter
                }
                onClearFilter={() => aggregateIssuesViewStore.clearFilters()}
              />
            )}
            {!aggregateIssuesViewStore.selectedIssueType &&
              aggregateIssuesViewStore.dataFilteredByIssueStatusCount !== 0 && (
                <IssuesTypeList viewStore={aggregateIssuesViewStore} />
              )}
            {aggregateIssuesViewStore.selectedIssueType && (
              <IssuesList aggregateIssuesViewStore={aggregateIssuesViewStore} />
            )}
          </Stack>
        </Container>
        <PositionMissingDialog store={aggregateIssuesViewStore} />
        <YardIssueResolveDialog
          issue={aggregateIssuesViewStore.selectedYardIssue}
          onClose={() => aggregateIssuesViewStore.toggleYardIssueResolve()}
          onCreateInternalWorkInstruction={params =>
            workInstructionService.createInternalWorkInstruction(params)
          }
        />
        <ResolveIssueDialog store={planningIssueResolutionViewStore} />
        <CompaniesDialogContainer />
      </Box>
    </InitializationWrapper>
  )
})
